/**
 * balance.ts — leer el balance de comprobación del sistema ANTERIOR.
 *
 * Es el primo de `apertura.ts` y hace falta que sean dos, no uno con una
 * bandera: el de apertura lee un archivo con NUESTROS códigos y sale listo para
 * la base; éste lee un archivo con LOS CÓDIGOS DEL CLIENTE y sale hacia una
 * pantalla de correspondencias. Lo único que comparten —partir un CSV con
 * comillas— se comparte de verdad: `partirCsv` vive en `apertura.ts` y se
 * importa.
 *
 * ▶ SE EXIGEN «debe» Y «haber». UNA SOLA COLUMNA DE SALDO SE RECHAZA.
 *
 * Muchos sistemas exportan un saldo con signo, y la tentación es aceptarlo:
 * positivo al debe, negativo al haber. **Esa convención no es universal.** Hay
 * sistemas que dan el saldo en valor absoluto con una columna de naturaleza
 * aparte, y otros que lo firman al revés según el tipo de cuenta.
 *
 * Adivinar mal invierte cuentas enteras, y lo hace en silencio: el asiento de
 * apertura **cuadra igual** —la suma total no cambia— y el balance queda con la
 * caja en el haber. No hay comprobación que atrape eso.
 *
 * Un balance de comprobación tiene por definición las dos columnas: es lo que
 * lo hace una comprobación. Si el archivo del cliente no las trae, se dice, y
 * se pide el que sí.
 *
 * ▶ LOS IMPORTES SE LEEN COMO TEXTO Y NO SE TOCAN.
 *
 * La misma regla que en todo el sistema. Acá importa un poco más que de
 * costumbre: estos números vienen de otro programa y van a ser el saldo inicial
 * de todo lo que venga después.
 */
import { partirCsv } from './apertura.ts';

/** Una fila del balance ajeno: su código, su nombre, sus dos columnas. */
export interface FilaDeBalance {
  readonly codigo: string;
  readonly nombre: string;
  readonly debe: string;
  readonly haber: string;
}

export type MotivoDeBalance =
  | 'sin_filas'        // no hay nada debajo de la cabecera
  | 'falta_codigo'     // la cabecera no trae la columna del código
  | 'solo_saldo'       // trae `saldo` pero no `debe`/`haber`
  | 'sin_importe'      // una fila sin ninguna de las dos
  | 'sin_lineas';      // había filas y ninguna con código

export type LecturaDeBalance =
  | { ok: true; filas: FilaDeBalance[] }
  | { ok: false; motivo: MotivoDeBalance; fila?: number; dato?: string };

/** Los nombres que estos archivos suelen traer, en español y en inglés. */
const COLUMNAS = {
  codigo: ['codigo', 'código', 'cuenta', 'code', 'account'],
  nombre: ['nombre', 'descripcion', 'descripción', 'name', 'description'],
  debe: ['debe', 'debito', 'débito', 'debit', 'debe_total'],
  haber: ['haber', 'credito', 'crédito', 'credit', 'haber_total'],
  saldo: ['saldo', 'balance', 'saldo_final'],
} as const;

export function leerBalance(crudo: string): LecturaDeBalance {
  const texto = crudo.replace(/^\uFEFF/, '');
  const filas = texto.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (filas.length < 2) return { ok: false, motivo: 'sin_filas' };

  const cabecera = partirCsv(filas[0] ?? '').map((h) => h.toLowerCase());
  /** El índice de la primera columna que se llame de alguna de esas formas. */
  const donde = (nombres: readonly string[]) => {
    for (const n of nombres) {
      const i = cabecera.indexOf(n);
      if (i >= 0) return i;
    }
    return -1;
  };

  const iCodigo = donde(COLUMNAS.codigo);
  if (iCodigo < 0) {
    return { ok: false, motivo: 'falta_codigo', dato: cabecera.join(', ') };
  }

  const iDebe = donde(COLUMNAS.debe);
  const iHaber = donde(COLUMNAS.haber);
  if (iDebe < 0 || iHaber < 0) {
    // Ver la cabecera: con una sola columna de saldo habría que adivinar la
    // convención de signo, y adivinar mal invierte cuentas sin que nada falle.
    const conSaldo = donde(COLUMNAS.saldo) >= 0;
    return {
      ok: false,
      motivo: conSaldo ? 'solo_saldo' : 'falta_codigo',
      dato: cabecera.join(', '),
    };
  }

  const iNombre = donde(COLUMNAS.nombre);
  const salida: FilaDeBalance[] = [];

  for (let i = 1; i < filas.length; i++) {
    const c = partirCsv(filas[i] ?? '');
    const codigo = (c[iCodigo] ?? '').trim();
    if (codigo === '') continue;

    const debe = (c[iDebe] ?? '').trim();
    const haber = (c[iHaber] ?? '').trim();

    // ▶ Un cero explícito NO es una fila sin importe.
    //
    // Estos archivos suelen traer `0.00` en la columna que no aplica, y
    // rechazarlas obligaría a limpiar el archivo a mano antes de subirlo — que
    // es justo el trabajo que esta pantalla existe para quitar.
    if (debe === '' && haber === '') {
      return { ok: false, motivo: 'sin_importe', fila: i + 1, dato: codigo };
    }

    salida.push({
      codigo,
      nombre: iNombre >= 0 ? (c[iNombre] ?? '').trim() : '',
      debe,
      haber,
    });
  }

  if (salida.length === 0) return { ok: false, motivo: 'sin_lineas' };
  return { ok: true, filas: salida };
}

/**
 * Una sugerencia de correspondencia por parecido de nombre.
 *
 * ▶ SUGIERE. NO DECIDE, Y LA DIFERENCIA ES TODA LA PANTALLA.
 *
 * Nadie puede saber por el nombre que «CAJA CHICA» del sistema viejo es
 * nuestra 1101 y no una auxiliar que el cliente va a crear. Lo que sí se puede
 * es ahorrarle buscar en una lista de noventa y cuatro: la sugerencia llega
 * elegida en el desplegable **solo cuando el parecido es alto**, y por debajo
 * de ese umbral el campo llega vacío a propósito — una sugerencia floja
 * aceptada sin mirar es peor que ninguna, porque nadie revisa lo que ya está
 * lleno.
 */
export function sugerir(
  nombreExterno: string,
  cuentas: ReadonlyArray<{ codigo: string; nombre: string }>,
): string | null {
  const limpio = normalizar(nombreExterno);
  if (limpio.length < 3) return null;

  let mejor: { codigo: string; puntos: number } | null = null;
  for (const c of cuentas) {
    const puntos = parecido(limpio, normalizar(c.nombre));
    if (!mejor || puntos > mejor.puntos) mejor = { codigo: c.codigo, puntos };
  }
  // 0,72 no sale de una teoría: es el umbral que deja pasar «CAJA GENERAL» →
  // «Caja general» y detiene «CAJA CHICA» → «Caja general», que es exactamente
  // la pareja que no se debe resolver sola.
  return mejor && mejor.puntos >= 0.72 ? mejor.codigo : null;
}

/** Sin tildes, sin puntuación y en minúsculas. Comparar «Depreciación» con «depreciacion». */
function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Dice cuánto se parecen dos textos, por palabras compartidas (Sørensen–Dice). */
function parecido(a: string, b: string): number {
  const pa = new Set(a.split(' ').filter(Boolean));
  const pb = new Set(b.split(' ').filter(Boolean));
  if (pa.size === 0 || pb.size === 0) return 0;
  let comunes = 0;
  for (const p of pa) if (pb.has(p)) comunes++;
  return (2 * comunes) / (pa.size + pb.size);
}
